import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDashboardCards, useAccountTypeChoices, useTemplateSummary } from "../hooks";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {  Eye, Search, Filter, LayoutTemplate, CheckCircle, Activity } from "lucide-react";
import { DashboardCard } from "../types";
import { PageHeader } from "@shared/components/PageHeader";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";

export default function BillTemplateLanding() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<{ accountType?: string; status?: string }>({});
  
  const { data, } = useDashboardCards({ search, ...activeFilters });
  const { data: accountTypeChoices = [] } = useAccountTypeChoices();
  const { data: summaryData } = useTemplateSummary();
  const [showFilters, setShowFilters] = useState(false);

  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "All",
        options: [
          { value: "True", label: "Active" },
          { value: "False", label: "Inactive" },
        ],
        className: "col-span-1",
      },
    ],
    [accountTypeChoices]
  );

  const filterForm = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleFilterSubmit = (data: any) => {
    setActiveFilters({
      accountType: data.accountType,
      status: data.status,
    });
    console.log("Filters applied:", data);
  };

  const handleClearFilters = () => {
    filterForm.reset();
    setActiveFilters({});
  };


  const onPreview = (card: DashboardCard) => {
    navigate(`/bx/bill-setup/billtemplate/detail/${card.templateId}`, { 
      state: { cardType: card.cardType,name:card.title,description:card.description} 
    });
  };

  const cards: DashboardCard[] = data?.result || [];

  return (
    <div>
      <PageHeader 
        title={"Bill Templates"} 
        subtitle="Reusable billing structures for each utility service and consumer type"
        buttonName="Create New Template"
        handleSubmit={() => navigate("template-setup/create")}
      />
      
      {/* Stats Cards */}
      <div className="px-6 mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickStatsCard
          title="Total Templates"
          count={summaryData?.totalTemplates ?? "NA"}
          icon={<LayoutTemplate className="w-8 h-8 text-blue-500" />}
        />
        <QuickStatsCard
          title="Active"
          count={summaryData?.active ?? "NA"}
          icon={<CheckCircle className="w-8 h-8 text-green-500" />}
        />
        <QuickStatsCard
          title="Services"
          count={summaryData?.services ?? "NA"}
          icon={<Activity className="w-8 h-8 text-purple-500" />}
        />
      </div>

      {/* Search and Filter Row */}
      <div className="px-6 mt-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="relative w-full md:w-auto md:flex-1 py-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" size={18} />
            <Input 
              placeholder="Search templates..." 
              className="pl-10 w-full md:w-96"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex gap-1"
            >
              <Filter size={16} />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 border rounded-md p-4">
            <DynamicForm
              fields={filterFields}
              form={filterForm}
              onSubmit={handleFilterSubmit}
              gridColumns={2}
              config={{
                layout: "vertical",
                showSubmitButton: false,
                showResetButton: false,
                classes: {
                  form: "grid",
                  fieldContainer: "w-full",
                },
              }}
            />

            {/* Filter Actions */}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                Clear
              </Button>
              <Button size="sm" onClick={() => handleFilterSubmit(filterForm.getValues())}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
        {cards.map((card) => (
          <div
            key={card.templateId}
            className="flex flex-col rounded-xl border border-blue-600 bg-white shadow-sm overflow-hidden"
          >
            {/* Top blue border effect like in the image */}
            <div className="h-1 bg-blue-600 w-full"></div>

            <div className="p-6 flex-1 flex flex-col">

              {/* Title & Description */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {card?.title}
              </h3>
              <p className="text-sm text-gray-500 mb-6 flex-1">
                {card?.description}
              </p>

              {/* Status & Last Modified */}
              <div className="flex items-center justify-between mb-6">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${
                    card?.status?.toLowerCase() === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      card?.status?.toLowerCase() === "active"
                        ? "bg-green-500"
                        : "bg-gray-400"
                    }`}
                  ></span>
                  {card?.status}
                </span>
                
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 border-t border-gray-100 pt-5 mt-auto">
                <Button
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2 text-gray-700"
                  onClick={() => onPreview(card)}
                >
                  <Eye className="h-4 w-4" />
                  Preview
                  
                </Button>
                
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
